import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, MapPin, Calendar, Ruler, Package } from 'lucide-react';

const ProjectDetailsModal = ({ project, isOpen, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !project) return null;

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === project.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? project.images.length - 1 : prev - 1
    );
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.8)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'white',
          borderRadius: '20px',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'hidden',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '20px',
            right: '20px',
            background: 'rgba(0, 0, 0, 0.5)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
            transition: 'background 0.3s ease'
          }}
          onMouseEnter={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.7)'}
          onMouseLeave={(e) => e.target.style.background = 'rgba(0, 0, 0, 0.5)'}
        >
          <X size={20} />
        </button>

        {/* Image Gallery */}
        <div style={{ 
          position: 'relative', 
          width: '100%',
          height: '0',
          paddingBottom: '66.67%', // 3:2 aspect ratio (2/3 = 0.6667)
          overflow: 'hidden'
        }}>
          <img
            src={project.images[currentImageIndex]}
            alt={`${project.title} - Image ${currentImageIndex + 1}`}
            style={{
              position: 'absolute',
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          
          {/* Image Navigation */}
          {project.images.length > 1 && (
            <>
              <button
                onClick={prevImage}
                style={{
                  position: 'absolute',
                  left: '20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  zIndex: 20
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.transform = 'translateY(-50%) scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.target.style.transform = 'translateY(-50%) scale(1)';
                }}
              >
                <ChevronLeft size={24} color="var(--brand-dark)" />
              </button>

              <button
                onClick={nextImage}
                style={{
                  position: 'absolute',
                  right: '20px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '50px',
                  height: '50px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  zIndex: 20
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'white';
                  e.target.style.transform = 'translateY(-50%) scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'rgba(255, 255, 255, 0.9)';
                  e.target.style.transform = 'translateY(-50%) scale(1)';
                }}
              >
                <ChevronRight size={24} color="var(--brand-dark)" />
              </button>

              {/* Image Indicators */}
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '8px'
              }}>
                {project.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      border: 'none',
                      background: currentImageIndex === index ? 'var(--brand-primary)' : 'rgba(255, 255, 255, 0.7)',
                      cursor: 'pointer',
                      transition: 'background 0.3s ease'
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Project Details */}
        <div style={{ padding: '40px', maxHeight: 'calc(90vh - 400px)', overflowY: 'auto' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: 700,
            color: 'var(--brand-dark)',
            marginBottom: '20px'
          }}>
            {project.title}
          </h2>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            marginBottom: '30px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                background: 'var(--brand-primary)',
                borderRadius: '8px',
                padding: '8px',
                display: 'flex'
              }}>
                <Ruler size={20} color="var(--brand-dark)" />
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Built Area</div>
                <div style={{ fontWeight: 600, color: 'var(--brand-dark)' }}>{project.builtArea}</div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                background: 'var(--brand-primary)',
                borderRadius: '8px',
                padding: '8px',
                display: 'flex'
              }}>
                <MapPin size={20} color="var(--brand-dark)" />
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Location</div>
                <div style={{ fontWeight: 600, color: 'var(--brand-dark)' }}>{project.location}</div>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                background: 'var(--brand-primary)',
                borderRadius: '8px',
                padding: '8px',
                display: 'flex'
              }}>
                <Calendar size={20} color="var(--brand-dark)" />
              </div>
              <div>
                <div style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>Completed</div>
                <div style={{ fontWeight: 600, color: 'var(--brand-dark)' }}>{project.completionDate}</div>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '30px' }}>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: 'var(--brand-dark)',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Package size={18} /> Materials Used
            </h3>
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              {project.materials.map((material, index) => (
                <span
                  key={index}
                  style={{
                    background: 'rgba(211, 255, 98, 0.2)',
                    color: 'var(--brand-dark)',
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    border: '1px solid rgba(211, 255, 98, 0.4)'
                  }}
                >
                  {material}
                </span>
              ))}
            </div>
          </div>

          <div>
            <h3 style={{
              fontSize: '1.125rem',
              fontWeight: 600,
              color: 'var(--brand-dark)',
              marginBottom: '12px'
            }}>
              Project Description
            </h3>
            <p style={{
              color: 'var(--text-secondary)',
              lineHeight: 1.6,
              fontSize: '1rem'
            }}>
              {project.description}
            </p>
          </div>
        </div>
      </div>

      <style jsx>{`
        @media (max-width: 768px) {
          div[style*="maxWidth: '900px'"] {
            margin: 10px;
            max-height: 95vh;
          }
          
          div[style*="paddingBottom: '66.67%'"] {
            /* Maintain aspect ratio on mobile */
          }
          
          div[style*="padding: '40px'"] {
            padding: 20px !important;
          }
          
          div[style*="gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))'"] {
            grid-template-columns: 1fr !important;
            gap: 15px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ProjectDetailsModal;